import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MousePositionService } from '../../services/mouse-position.service';

@Component({
  selector: 'app-safe-area',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      style="position: fixed; pointer-events: none; z-index: 11;"
      [style.width.px]="geo().width"
      [style.height.px]="geo().height"
      [style.top.px]="geo().top"
      [style.left.px]="geo().left"
    >
      <path
        stroke-width="1"
        style="pointer-events: auto;"
        [attr.d]="geo().d"
        [attr.fill]="debug ? 'rgba(255, 100, 0, 0.3)' : 'transparent'"
        [attr.stroke]="debug ? 'red' : 'none'"
        (click)="passClickThrough($event)"
      />
    </svg>
  `,
})
export class SafeAreaComponent {
  public readonly anchor = input.required<HTMLElement>();
  public readonly submenu = input.required<HTMLElement>();
  protected readonly debug = false;

  private readonly mouse = toSignal(inject(MousePositionService).mousePosition$, {
    initialValue: { x: 0, y: 0 },
  });

  protected readonly geo = computed(() => {
    const m = this.mouse();
    const anchorRect = this.anchor().getBoundingClientRect();
    const subRect = this.submenu().getBoundingClientRect();
    const svgWidth = subRect.left - m.x + 4;

    if (svgWidth <= 0) {
      return { width: 0, height: 0, top: 0, left: 0, d: '' };
    }

    const clampedY = Math.max(anchorRect.top, Math.min(anchorRect.bottom, m.y));

    return {
      width: svgWidth,
      height: subRect.height,
      top: subRect.top,
      left: m.x - 2,
      d: `M 0,${clampedY - subRect.top} L ${svgWidth},${subRect.height} L ${svgWidth},0 z`,
    };
  });

  protected passClickThrough(event: MouseEvent): void {
    const path = event.currentTarget as SVGPathElement;
    path.style.pointerEvents = 'none';
    const target = document.elementFromPoint(event.clientX, event.clientY);
    path.style.pointerEvents = 'auto';

    if (target && target !== path) {
      target.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: event.clientX,
          clientY: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
        }),
      );
    }
  }
}
